import { fn } from '@ember/helper';
import Component from '@glimmer/component';
import element from 'ember-element-helper/helpers/element';
import or from 'ember-truth-helpers/helpers/or';
import BvMenuItem from 'volta/components/bv-menu/item';
import classNames from 'volta/helpers/class-names';

import type { BvMenuItemSignature } from 'volta/components/bv-menu/item';
import type { ComponentLike, WithBoundArgs } from '@glint/template';
import type { IBvMenuItem, IBvMenuSection } from 'volta/components/bv-menu';

export interface BvMenuSectionSignature {
  Args: IBvMenuSection & {
    // Whether the parent action list has multiple sections
    hasMultipleSections?: boolean;
    onAnyItemAction?: (event: Event) => void;
    onItemMouseEnter: (event: Event) => void;
  };
  Blocks: {
    default: [WithBoundArgs<ComponentLike<BvMenuItemSignature>, 'onMouseEnter'>];
    title: [];
  };
}

export default class BvMenuSectionComponent extends Component<BvMenuSectionSignature> {
  onItemAction = (item: IBvMenuItem, event: Event) => {
    event.preventDefault();
    event.stopPropagation();
    item.onClick?.(event);
    this.args.onAnyItemAction?.(event);
  };

  <template>
    {{#let (element (if @hasMultipleSections 'li' 'div')) as |Tag|}}
      <Tag class={{classNames 'bv-menu-section' (unless @title 'bv-menu-section--without-title')}}>
        {{#if (or @title (has-block 'title'))}}
          <p class='bv-menu-title'>
            {{#if @title}}
              {{@title}}
            {{else}}
              {{yield to='title'}}
            {{/if}}
          </p>
        {{/if}}
        <ul class='bv-menu__items' role='menu' tabindex='-1'>
          {{#each @items as |item|}}
            {{#if item}}
              <BvMenuItem
                @hasArrow={{item.hasArrow}}
                @isLoading={{item.isLoading}}
                @active={{item.active}}
                @href={{item.href}}
                @model={{item.model}}
                @icon={{item.icon}}
                @iconColor={{item.iconColor}}
                @content={{item.content}}
                @suffix={{item.suffix}}
                @color={{item.color}}
                @selected={{item.selected}}
                @disabled={{item.disabled}}
                @tooltip={{item.tooltip}}
                @title={{item.title}}
                @badge={{item.badge}}
                @prefix={{item.prefix}}
                @shortcut={{item.shortcut}}
                @onClick={{fn this.onItemAction item}}
                @onMouseEnter={{@onItemMouseEnter}}
              />
            {{/if}}
          {{/each}}
          {{#if (has-block)}}
            {{yield (component BvMenuItem onMouseEnter=@onItemMouseEnter)}}
          {{/if}}
        </ul>
      </Tag>
    {{/let}}
  </template>
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'BvMenu::Section': typeof BvMenuSectionComponent;
    'bv-menu/section': typeof BvMenuSectionComponent;
  }
}
